import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { api, AlertRule, NotifChannel, NotifHistoryItem, AlertMetric } from "../api";
import { useRole } from "../contexts/AuthContext";

// ── metric helpers ─────────────────────────────────────────────────────────

function fmtBps(v: number): string {
  if (v >= 1e9) return (v / 1e9).toFixed(1) + " Gbps";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + " Mbps";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " kbps";
  return v + " bps";
}
function fmtPps(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(1) + " Mpps";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " kpps";
  return v + " pps";
}
function fmtValue(metric: string, v: number): string {
  return metric === "bps_in" || metric === "bps_out" ? fmtBps(v) : fmtPps(v);
}
function metricLabel(m: string): string {
  switch (m) {
    case "bps_in": return "Inbound bps";
    case "bps_out": return "Outbound bps";
    case "pps_in": return "Inbound pps";
    case "pps_out": return "Outbound pps";
    default: return m;
  }
}
function isBpsMetric(m: string) {
  return m === "bps_in" || m === "bps_out";
}

// ── SecondsInput ───────────────────────────────────────────────────────────
// InputNumber + "s" suffix as a proper controlled component so Form.Item
// binding works correctly (avoids the addonAfter value-undefined bug in AntD 5).

interface SecondsInputProps {
  value?: number;
  onChange?: (v: number) => void;
}
function SecondsInput({ value, onChange }: SecondsInputProps) {
  return (
    <Space.Compact style={{ width: "100%" }}>
      <InputNumber
        min={0}
        value={value}
        onChange={(v) => onChange?.(v ?? 0)}
        style={{ width: "100%" }}
      />
      <span style={{
        display: "inline-flex", alignItems: "center",
        padding: "0 11px",
        background: "#fafafa", border: "1px solid #d9d9d9",
        borderLeft: "none", borderRadius: "0 6px 6px 0",
        color: "#888", fontSize: 13,
      }}>s</span>
    </Space.Compact>
  );
}

// ── ThresholdInput ─────────────────────────────────────────────────────────
// Lets users enter "100 Mbps" instead of raw 100000000.

const BPS_UNITS = [
  { label: "bps",  mul: 1 },
  { label: "kbps", mul: 1_000 },
  { label: "Mbps", mul: 1_000_000 },
  { label: "Gbps", mul: 1_000_000_000 },
];
const PPS_UNITS = [
  { label: "pps",  mul: 1 },
  { label: "kpps", mul: 1_000 },
  { label: "Mpps", mul: 1_000_000 },
];

function decomposeThreshold(metric: string, raw: number) {
  const units = isBpsMetric(metric) ? BPS_UNITS : PPS_UNITS;
  for (let i = units.length - 1; i >= 0; i--) {
    if (raw >= units[i].mul && raw % units[i].mul === 0) {
      return { displayVal: raw / units[i].mul, unit: units[i].label };
    }
  }
  return { displayVal: raw, unit: units[0].label };
}

interface ThresholdInputProps {
  metric: AlertMetric;
  value?: number;
  onChange?: (v: number) => void;
}
function ThresholdInput({ metric, value = 0, onChange }: ThresholdInputProps) {
  const units = isBpsMetric(metric) ? BPS_UNITS : PPS_UNITS;
  const { displayVal, unit } = decomposeThreshold(metric, value);
  const [display, setDisplay] = useState(displayVal);
  const [selUnit, setSelUnit] = useState(unit);

  // Sync if value changes externally (e.g. form reset).
  useEffect(() => {
    const d = decomposeThreshold(metric, value);
    setDisplay(d.displayVal);
    setSelUnit(d.unit);
  }, [metric, value]);

  function emit(d: number, u: string) {
    const mul = units.find((x) => x.label === u)?.mul ?? 1;
    onChange?.(Math.round(d * mul));
  }

  return (
    <Space.Compact style={{ width: "100%" }}>
      <InputNumber
        style={{ width: "65%" }}
        min={0}
        value={display}
        onChange={(v) => {
          const d = v ?? 0;
          setDisplay(d);
          emit(d, selUnit);
        }}
      />
      <Select
        style={{ width: "35%" }}
        value={selUnit}
        options={units.map((u) => ({ value: u.label, label: u.label }))}
        onChange={(u) => {
          setSelUnit(u);
          emit(display, u);
        }}
      />
    </Space.Compact>
  );
}

// ── ChannelModal ───────────────────────────────────────────────────────────

interface ChannelModalProps {
  open: boolean;
  editing: NotifChannel | null;
  onClose: () => void;
  onSaved: () => void;
}
function ChannelModal({ open, editing, onClose, onSaved }: ChannelModalProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [chType, setChType] = useState<"telegram" | "webhook">("telegram");

  useEffect(() => {
    if (open) {
      if (editing) {
        form.setFieldsValue({
          name: editing.name,
          type: editing.type,
          enabled: editing.enabled,
          bot_token: editing.config.bot_token ?? "",
          chat_id: editing.config.chat_id ?? "",
          url: editing.config.url ?? "",
          secret: editing.config.secret ?? "",
        });
        setChType(editing.type);
      } else {
        form.resetFields();
        form.setFieldValue("type", "telegram");
        form.setFieldValue("enabled", true);
        setChType("telegram");
      }
    }
  }, [open, editing, form]);

  async function handleOk() {
    const vals = await form.validateFields();
    setSaving(true);
    try {
      const config: Record<string, string> =
        vals.type === "telegram"
          ? { bot_token: vals.bot_token ?? "", chat_id: vals.chat_id ?? "" }
          : { url: vals.url ?? "", secret: vals.secret ?? "" };

      const body = {
        name: vals.name,
        type: vals.type,
        config,
        enabled: vals.enabled ?? true,
      };
      if (editing) {
        await api.updateNotifChannel(editing.id, body);
        message.success("Channel updated");
      } else {
        await api.createNotifChannel(body);
        message.success("Channel created");
      }
      onSaved();
      onClose();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={editing ? "Edit Channel" : "New Notification Channel"}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={saving}
      okText={editing ? "Save" : "Create"}
      width={500}
    >
      <Form form={form} layout="vertical" size="small">
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. My Telegram Bot" />
        </Form.Item>
        <Form.Item name="type" label="Type" rules={[{ required: true }]}>
          <Select
            disabled={!!editing}
            options={[
              { value: "telegram", label: "Telegram" },
              { value: "webhook", label: "Webhook (HTTP POST)" },
            ]}
            onChange={(v) => setChType(v)}
          />
        </Form.Item>

        {chType === "telegram" && (
          <>
            <Form.Item
              name="bot_token"
              label="Bot Token"
              rules={[{ required: !editing }]}
            >
              <Input.Password
                placeholder={editing ? "leave blank to keep existing" : "123456:ABC-DEF..."}
              />
            </Form.Item>
            <Form.Item
              name="chat_id"
              label="Chat ID"
              rules={[{ required: true }]}
            >
              <Input placeholder="-100123456789 or @username" />
            </Form.Item>
          </>
        )}

        {chType === "webhook" && (
          <>
            <Form.Item
              name="url"
              label="Webhook URL"
              rules={[{ required: true, type: "url" }]}
            >
              <Input placeholder="https://hooks.example.com/alert" />
            </Form.Item>
            <Form.Item name="secret" label="Secret (optional)">
              <Input placeholder="Sent as X-Secret header" />
            </Form.Item>
          </>
        )}

        <Form.Item name="enabled" label="Enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── RuleModal ──────────────────────────────────────────────────────────────

interface RuleModalProps {
  open: boolean;
  editing: AlertRule | null;
  channels: NotifChannel[];
  onClose: () => void;
  onSaved: () => void;
}
function RuleModal({ open, editing, channels, onClose, onSaved }: RuleModalProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [targetType, setTargetType] = useState<"global" | "subnet" | "ip">("global");
  const [metric, setMetric] = useState<AlertMetric>("bps_in");
  const [threshold, setThreshold] = useState(0);

  useEffect(() => {
    if (open) {
      if (editing) {
        setTargetType(editing.target_type);
        setMetric(editing.metric);
        setThreshold(editing.threshold);
        form.setFieldsValue({
          name: editing.name,
          enabled: editing.enabled,
          target_type: editing.target_type,
          target_value: editing.target_value,
          metric: editing.metric,
          sustained_s: editing.sustained_s,
          cooldown_s: editing.cooldown_s,
          notify_resolve: editing.notify_resolve,
          channel_id: editing.channel_id,
          message_template: editing.message_template ?? "",
        });
      } else {
        setTargetType("global");
        setMetric("bps_in");
        setThreshold(0);
        form.resetFields();
        form.setFieldsValue({
          enabled: true,
          target_type: "global",
          target_value: "",
          metric: "bps_in",
          sustained_s: 60,
          cooldown_s: 300,
          notify_resolve: true,
          message_template: "",
        });
      }
    }
  }, [open, editing, form]);

  async function handleOk() {
    const vals = await form.validateFields();
    setSaving(true);
    try {
      const body = {
        name: vals.name,
        enabled: vals.enabled ?? true,
        target_type: vals.target_type,
        target_value: vals.target_type === "subnet" ? (vals.target_value ?? "") : "",
        metric: vals.metric,
        threshold,
        sustained_s: vals.sustained_s ?? 60,
        cooldown_s: vals.cooldown_s ?? 300,
        notify_resolve: vals.notify_resolve ?? true,
        channel_id: vals.channel_id,
        message_template: vals.message_template ?? "",
      };
      if (editing) {
        await api.updateNotifRule(editing.id, body);
        message.success("Rule updated");
      } else {
        await api.createNotifRule(body);
        message.success("Rule created");
      }
      onSaved();
      onClose();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={editing ? "Edit Alert Rule" : "New Alert Rule"}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={saving}
      okText={editing ? "Save" : "Create"}
      width={560}
    >
      <Form form={form} layout="vertical" size="small">
        <Form.Item name="name" label="Rule Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Global Inbound > 1 Gbps" />
        </Form.Item>

        <Form.Item name="target_type" label="Target" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "global", label: "🌐 Global (all traffic)" },
              { value: "subnet", label: "🗂 Subnet / CIDR" },
              { value: "ip",     label: "📍 Per-IP (any single IP)" },
            ]}
            onChange={(v) => setTargetType(v)}
          />
        </Form.Item>

        {targetType === "subnet" && (
          <Form.Item
            name="target_value"
            label="CIDR"
            rules={[
              { required: true },
              {
                pattern: /^[\d.:a-fA-F]+\/\d+$/,
                message: "Enter a valid CIDR, e.g. 10.0.0.0/8",
              },
            ]}
          >
            <Input placeholder="10.0.0.0/8" style={{ fontFamily: "monospace" }} />
          </Form.Item>
        )}

        {targetType === "ip" && (
          <div style={{
            padding: "8px 12px",
            background: "rgba(99,102,241,0.06)",
            border: "1px solid rgba(99,102,241,0.2)",
            borderRadius: 6,
            fontSize: 12,
            color: "#6366f1",
            marginBottom: 16,
          }}>
            📡 The engine scans <b>all IPs</b> seen in live flow data every 5s.
            Alert fires when <b>any single IP</b> exceeds the threshold.
            The culprit IP will be named in the notification.
          </div>
        )}

        <Form.Item name="metric" label="Metric" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "bps_in",  label: "Inbound bps" },
              { value: "bps_out", label: "Outbound bps" },
              { value: "pps_in",  label: "Inbound pps" },
              { value: "pps_out", label: "Outbound pps" },
            ]}
            onChange={(v) => {
              setMetric(v);
              // reset threshold when switching metric family
              setThreshold(0);
            }}
          />
        </Form.Item>

        <Form.Item label="Threshold (fires when exceeded)">
          <ThresholdInput
            metric={metric}
            value={threshold}
            onChange={setThreshold}
          />
          <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
            Preview: <b>{fmtValue(metric, threshold)}</b>
          </div>
        </Form.Item>

        <Form.Item
          name="sustained_s"
          label="Sustained (seconds)"
          tooltip="Must exceed threshold continuously for this many seconds before firing. Prevents flash-spike false alerts."
          rules={[{ required: true }]}
        >
          <SecondsInput />
        </Form.Item>

        <Form.Item
          name="cooldown_s"
          label="Cooldown (seconds)"
          tooltip="After firing, suppress further alerts for this many seconds."
          rules={[{ required: true }]}
        >
          <SecondsInput />
        </Form.Item>

        <Form.Item
          name="notify_resolve"
          label="Send RESOLVED notification"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="channel_id"
          label="Notification Channel"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Select channel"
            options={channels.map((c) => ({
              value: c.id,
              label: (
                <span>
                  {c.name}&nbsp;
                  <Tag style={{ fontSize: 10 }}>{c.type}</Tag>
                  {!c.enabled && <Tag color="default">disabled</Tag>}
                </span>
              ),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="message_template"
          label="Custom Message Template (optional)"
          tooltip="Go text/template syntax. Leave blank to use the built-in format."
        >
          <Input.TextArea
            rows={4}
            placeholder={"🔴 {{.Rule}} fired!\nMetric: {{.Metric}}  Value: {{.Value}}\nThreshold: {{.Threshold}}\n{{if .TopIP}}Top IP: {{.TopIP}}{{end}}"}
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
        </Form.Item>

        <Collapse
          ghost
          size="small"
          style={{ marginBottom: 12 }}
          items={[{
            key: "vars",
            label: <span style={{ fontSize: 12, color: "#888" }}>📖 Available template variables</span>,
            children: (
              <table style={{ fontSize: 11, borderCollapse: "collapse", width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "2px 8px", fontFamily: "monospace" }}>Variable</th>
                    <th style={{ textAlign: "left", padding: "2px 8px" }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {([
                    ["{{.Event}}",     "firing | resolved | test"],
                    ["{{.Rule}}",      "Rule name"],
                    ["{{.Metric}}",    "bps_in / bps_out / pps_in / pps_out"],
                    ["{{.Value}}",     "Formatted value (e.g. 1.2 Gbps)"],
                    ["{{.Threshold}}", "Formatted threshold"],
                    ["{{.Target}}",    "global | subnet CIDR | Per-IP"],
                    ["{{.TopIP}}",     "IP that triggered (ip-type rules)"],
                    ["{{.IPCount}}",   "Number of IPs firing (ip-type rules)"],
                    ["{{.Time}}",      "Timestamp"],
                    ["{{.Duration}}",  "How long above threshold"],
                  ] as [string, string][]).map(([v, d]) => (
                    <tr key={v}>
                      <td style={{ padding: "2px 8px", fontFamily: "monospace", color: "#6366f1" }}>{v}</td>
                      <td style={{ padding: "2px 8px", color: "#555" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ),
          }]}
        />

        <Form.Item name="enabled" label="Enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── ChannelsTab ────────────────────────────────────────────────────────────

interface ChannelsTabProps {
  onChannelsChange: (ch: NotifChannel[]) => void;
}
function ChannelsTab({ onChannelsChange }: ChannelsTabProps) {
  const [channels, setChannels] = useState<NotifChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<NotifChannel | null>(null);
  const { isOperator, isAdmin } = useRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.notifChannels();
      setChannels(data);
      onChannelsChange(data);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to load channels");
    } finally {
      setLoading(false);
    }
  }, [onChannelsChange]);

  useEffect(() => { load(); }, [load]);

  async function handleTest(id: number) {
    setTesting(id);
    try {
      await api.testNotifChannel(id);
      message.success("Test message sent!");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Test failed");
    } finally {
      setTesting(null);
    }
  }

  async function handleDelete(id: number) {
    try {
      await api.deleteNotifChannel(id);
      message.success("Channel deleted");
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Delete failed");
    }
  }

  const columns: ColumnsType<NotifChannel> = [
    { title: "ID", dataIndex: "id", width: 60 },
    { title: "Name", dataIndex: "name", render: (v, r) => (
      <Space>
        {r.type === "telegram" ? "📨" : "🔗"}
        <b>{v}</b>
      </Space>
    )},
    { title: "Type", dataIndex: "type", width: 100, render: (v) => <Tag>{v}</Tag> },
    { title: "Config", dataIndex: "config", render: (cfg: Record<string, string>, r) =>
        r.type === "telegram"
          ? <span style={{ fontFamily: "monospace", fontSize: 11 }}>chat_id: {cfg.chat_id}</span>
          : <span style={{ fontFamily: "monospace", fontSize: 11 }}>{cfg.url}</span>
    },
    { title: "Enabled", dataIndex: "enabled", width: 80, render: (v) =>
        v ? <Tag color="green">ON</Tag> : <Tag color="default">OFF</Tag>
    },
    {
      title: "Actions",
      width: 180,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Send test message">
            <Button
              size="small"
              icon={<SendOutlined />}
              loading={testing === r.id}
              disabled={!isOperator}
              onClick={() => handleTest(r.id)}
            >
              Test
            </Button>
          </Tooltip>
          {isOperator && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditing(r); setModalOpen(true); }}
            />
          )}
          {isAdmin && (
            <Popconfirm
              title="Delete this channel?"
              description="Rules using it will still fire but delivery will fail."
              onConfirm={() => handleDelete(r.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {isOperator && (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => { setEditing(null); setModalOpen(true); }}
          >
            Add Channel
          </Button>
        )}
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>
      <Table
        size="small"
        dataSource={channels}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
      />
      <ChannelModal
        open={modalOpen}
        editing={editing}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </div>
  );
}

// ── RulesTab ───────────────────────────────────────────────────────────────

function statusBadge(status: string) {
  switch (status) {
    case "firing":  return <Badge status="error"   text={<b style={{ color: "#f5222d" }}>FIRING</b>} />;
    case "pending": return <Badge status="warning" text="PENDING" />;
    default:        return <Badge status="success" text="OK" />;
  }
}

interface RulesTabProps {
  channels: NotifChannel[];
}
function RulesTab({ channels }: RulesTabProps) {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const { isOperator, isAdmin } = useRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await api.notifRules());
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id: number) {
    try {
      await api.deleteNotifRule(id);
      message.success("Rule deleted");
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Delete failed");
    }
  }

  const columns: ColumnsType<AlertRule> = [
    { title: "Status", dataIndex: "status", width: 110, render: statusBadge },
    { title: "Name", dataIndex: "name", render: (v, r) => (
      <Space direction="vertical" size={0}>
        <b>{v}</b>
        {!r.enabled && <Tag color="default" style={{ fontSize: 10 }}>disabled</Tag>}
      </Space>
    )},
    { title: "Target", width: 160, render: (_, r) => {
        if (r.target_type === "global")
          return <Tag icon={<span style={{ marginRight: 3 }}>🌐</span>}>Global</Tag>;
        if (r.target_type === "subnet")
          return <Tag color="blue"  style={{ fontFamily: "monospace" }}>🗂 {r.target_value}</Tag>;
        if (r.target_type === "ip")
          return <Tag color="purple">📍 Any IP</Tag>;
        return <Tag>{r.target_value || r.target_type}</Tag>;
    }},
    { title: "Metric", dataIndex: "metric", width: 120, render: metricLabel },
    { title: "Threshold", dataIndex: "threshold", width: 120,
      render: (v, r) => fmtValue(r.metric, v) },
    { title: "Sustained", dataIndex: "sustained_s", width: 90,
      render: (v) => `${v}s` },
    { title: "Cooldown", dataIndex: "cooldown_s", width: 90,
      render: (v) => `${v}s` },
    { title: "Channel", dataIndex: "channel_name", width: 130 },
    { title: "Last fired", dataIndex: "last_fired", width: 130,
      render: (v) => v ? new Date(v).toLocaleString() : "—" },
    {
      title: "Actions",
      width: 100,
      render: (_, r) => (
        <Space size={4}>
          {isOperator && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditing(r); setModalOpen(true); }}
            />
          )}
          {isAdmin && (
            <Popconfirm
              title="Delete this rule?"
              onConfirm={() => handleDelete(r.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {isOperator && (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => { setEditing(null); setModalOpen(true); }}
          >
            Add Rule
          </Button>
        )}
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>
      <Table
        size="small"
        dataSource={rules}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1000 }}
      />
      <RuleModal
        open={modalOpen}
        editing={editing}
        channels={channels}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </div>
  );
}

// ── HistoryTab ─────────────────────────────────────────────────────────────

function HistoryTab() {
  const [items, setItems] = useState<NotifHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await api.notifHistory({ limit: 200 }));
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns: ColumnsType<NotifHistoryItem> = [
    { title: "Time", dataIndex: "sent_at", width: 160,
      render: (v) => new Date(v).toLocaleString() },
    { title: "Event", dataIndex: "event", width: 100, render: (v) => {
        if (v === "firing")   return <Tag color="red">🔴 FIRING</Tag>;
        if (v === "resolved") return <Tag color="green">✅ RESOLVED</Tag>;
        return <Tag>TEST</Tag>;
    }},
    { title: "Rule", dataIndex: "rule_name" },
    { title: "Channel", dataIndex: "channel_name", width: 140 },
    { title: "Value", width: 110, render: (_, r) =>
        r.event !== "test" ? fmtBps(r.value) : "—"
    },
    { title: "Status", dataIndex: "ok", width: 80, render: (ok) =>
        ok
          ? <CheckCircleOutlined style={{ color: "#52c41a" }} />
          : <CloseCircleOutlined style={{ color: "#f5222d" }} />
    },
    { title: "Error", dataIndex: "error", render: (e) =>
        e ? <span style={{ color: "#f5222d", fontSize: 11 }}>{e}</span> : null
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
        <span style={{ lineHeight: "24px", fontSize: 12, color: "#888" }}>
          Last 200 entries
        </span>
      </div>
      <Table
        size="small"
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 900 }}
      />
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function Notifications() {
  const [channels, setChannels] = useState<NotifChannel[]>([]);

  const tabItems = [
    {
      key: "channels",
      label: <span><WifiOutlined /> Channels</span>,
      children: <ChannelsTab onChannelsChange={setChannels} />,
    },
    {
      key: "rules",
      label: <span><BellOutlined /> Alert Rules</span>,
      children: <RulesTab channels={channels} />,
    },
    {
      key: "history",
      label: <span><HistoryOutlined /> History</span>,
      children: <HistoryTab />,
    },
  ];

  return (
    <div style={{ padding: "0 4px" }}>
      <h2 style={{ marginBottom: 16 }}>🔔 Notifications</h2>
      <Tabs defaultActiveKey="channels" items={tabItems} />
    </div>
  );
}
